/*
 * FG_FORMS — shared, reusable form framework for API-submission forms on
 * fujigravel.com (support-rider application, gravel-ride guide application, and
 * any future form that POSTs JSON to admin.fujigravel.com/api/public/...).
 *
 * This is the storefront half of the company's forms framework. The field
 * DEFINITIONS come from the admin API (public.forms) or are declared inline by a
 * section; this module turns a field-definition array into DOM controls,
 * validates them, collects answers keyed by field key, and POSTs JSON. It does
 * NOT know about support riders or guides — callers map collected values to
 * whatever payload their endpoint expects.
 *
 * Field definition shape (matches api/src/lib/form-fields.js):
 *   { key, label, type, required, options?, max_length?, min?, max?, help_text?,
 *     placeholder? }
 *   type ∈ text | email | phone | url | textarea | number | select | checkbox |
 *          checkbox_group
 *
 * Classic global script (no module/importmap) so plain section <script> IIFEs
 * can use it. Loaded via {{ 'form-framework.js' | asset_url | script_tag }}.
 */
(function () {
    if (window.FG_FORMS) return

    var TYPES_WITH_OPTIONS = { select: true, checkbox_group: true }

    // File fields upload to Google Drive via an Apps Script web app, then submit
    // the returned Drive URL as the field's value. Keeping files in the shared
    // Drive was an explicit requirement. 6MB is the Apps Script payload ceiling.
    var GAS_UPLOAD_URL = 'https://script.google.com/macros/s/AKfycbzxr5SNWHBJzY9s-kke0WWGyQHxb04EKbpwj8l3dP92KoYBNne6PaWrhTgsBi9j8inU/exec'
    var FILE_MAX_SIZE_MB = 6

    // Resolve to the uploaded file's direct Drive URL, or reject with a JP error.
    function uploadFileToDrive(file) {
        return new Promise(function (resolve, reject) {
            var reader = new FileReader()
            reader.onload = function () {
                // URLSearchParams (not multipart) avoids a CORS preflight to GAS.
                var body = new URLSearchParams()
                body.append('fileData', reader.result)
                body.append('fileName', file.name)
                body.append('mimeType', file.type)
                fetch(GAS_UPLOAD_URL, { method: 'POST', body: body })
                    .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text() })
                    .then(function (text) {
                        var result
                        try { result = JSON.parse(text) } catch (e) { throw new Error('サーバー応答が無効です') }
                        if (result && result.success) resolve(result.directUrl)
                        else reject(new Error((result && result.error) || 'アップロードに失敗しました'))
                    })
                    .catch(function (e) { reject(new Error('ネットワークエラー: ' + (e && e.message ? e.message : ''))) })
            }
            reader.onerror = function () { reject(new Error('ファイルの読み込みに失敗しました')) }
            reader.readAsDataURL(file)
        })
    }

    // Wire a file input to upload-on-change and mirror the resulting URL into its
    // hidden companion (which carries the submitted value). On failure the file
    // input is cleared so a `required` file re-blocks the buy button.
    function attachFileUpload(fileInput, hidden, nameSpan, field) {
        var maxMb = field.max_size_mb || FILE_MAX_SIZE_MB
        fileInput.addEventListener('change', function () {
            var file = fileInput.files && fileInput.files[0]
            hidden.value = ''
            if (nameSpan) { nameSpan.hidden = true; nameSpan.textContent = '' }
            if (!file) return
            if (file.size > maxMb * 1024 * 1024) {
                alert('ファイルサイズが大きすぎます（最大 ' + maxMb + 'MB）')
                fileInput.value = ''
                return
            }
            fileInput.disabled = true
            if (nameSpan) { nameSpan.hidden = false; nameSpan.textContent = 'アップロード中… ' + file.name }
            uploadFileToDrive(file).then(function (url) {
                hidden.value = url
                if (nameSpan) nameSpan.textContent = file.name + ' ✓'
                fileInput.disabled = false
            }).catch(function (e) {
                alert('アップロードに失敗しました: ' + (e && e.message ? e.message : ''))
                fileInput.value = ''
                hidden.value = ''
                if (nameSpan) { nameSpan.hidden = true; nameSpan.textContent = '' }
                fileInput.disabled = false
            })
        })
    }

    function el(tag, attrs, text) {
        var node = document.createElement(tag)
        if (attrs) {
            for (var k in attrs) {
                if (attrs[k] == null) continue
                if (k === 'class') node.className = attrs[k]
                else node.setAttribute(k, attrs[k])
            }
        }
        if (text != null) node.textContent = text
        return node
    }

    function fieldId(prefix, key) {
        return (prefix || 'fgform') + '-' + key
    }

    // Build the control(s) for one field and append to the wrapper. Returns the
    // wrapper element (also tagged with data-field-key / data-field-type).
    function renderField(field, opts) {
        opts = opts || {}
        var id = fieldId(opts.idPrefix, field.key)
        var wrap = el('div', { class: 'fgform-field', 'data-field-key': field.key, 'data-field-type': field.type })

        var labelText = field.label || field.key
        var isCheckbox = field.type === 'checkbox'

        if (!isCheckbox) {
            var label = el('label', { class: 'fgform-label', for: id }, labelText)
            if (field.required) label.appendChild(el('span', { class: 'fgform-required' }, '必須'))
            wrap.appendChild(label)
        }

        var control
        switch (field.type) {
            case 'textarea':
                control = el('textarea', {
                    id: id, name: field.key, class: 'fgform-input fgform-textarea', rows: '4',
                    maxlength: field.max_length || 1000,
                    placeholder: field.placeholder || '',
                    required: field.required ? 'required' : null,
                })
                wrap.appendChild(control)
                var counter = el('div', { class: 'fgform-counter' }, '0 / ' + (field.max_length || 1000))
                wrap.appendChild(counter)
                control.addEventListener('input', function () {
                    counter.textContent = control.value.length + ' / ' + (field.max_length || 1000)
                })
                break
            case 'select':
                control = el('select', {
                    id: id, name: field.key, class: 'fgform-input fgform-select',
                    required: field.required ? 'required' : null,
                })
                control.appendChild(el('option', { value: '' }, '選択してください'))
                ;(field.options || []).forEach(function (o) {
                    control.appendChild(el('option', { value: o }, o))
                })
                wrap.appendChild(control)
                break
            case 'checkbox':
                control = el('input', {
                    id: id, name: field.key, type: 'checkbox', value: '1',
                    class: 'fgform-checkbox',
                    required: field.required ? 'required' : null,
                })
                var inlineLabel = el('label', { class: 'fgform-check', for: id })
                inlineLabel.appendChild(control)
                inlineLabel.appendChild(el('span', null, labelText + (field.required ? ' （必須）' : '')))
                wrap.appendChild(inlineLabel)
                break
            case 'checkbox_group':
                var group = el('div', { class: 'fgform-check-list', 'data-checkbox-group-for': field.key })
                ;(field.options || []).forEach(function (o, i) {
                    var optId = id + '-' + i
                    var optLabel = el('label', { class: 'fgform-check', for: optId })
                    var cb = el('input', { id: optId, type: 'checkbox', value: o, 'data-checkbox-group': field.key })
                    optLabel.appendChild(cb)
                    optLabel.appendChild(el('span', null, o))
                    group.appendChild(optLabel)
                })
                wrap.appendChild(group)
                break
            case 'number':
                control = el('input', {
                    id: id, name: field.key, type: 'number', class: 'fgform-input',
                    min: field.min != null ? field.min : null,
                    max: field.max != null ? field.max : null,
                    placeholder: field.placeholder || '',
                    required: field.required ? 'required' : null,
                })
                wrap.appendChild(control)
                break
            case 'file':
                // The file input itself is never submitted (no name); the hidden
                // companion carries the uploaded Drive URL. The hidden is named by
                // field key here (API mode); cart mode renames it to the property.
                control = el('input', {
                    id: id, type: 'file', class: 'fgform-input fgform-file',
                    accept: (field.accepted_formats || []).join(',') || null,
                    required: field.required ? 'required' : null,
                })
                var fileHidden = el('input', { type: 'hidden', name: field.key, 'data-file-url': field.key, value: '' })
                var fileName = el('span', { class: 'fgform-file-name', hidden: 'hidden' })
                wrap.appendChild(control)
                wrap.appendChild(fileName)
                wrap.appendChild(fileHidden)
                attachFileUpload(control, fileHidden, fileName, field)
                break
            default: // text / email / phone / url / date
                var inputType = field.type === 'email' ? 'email'
                    : field.type === 'phone' ? 'tel'
                    : field.type === 'date' ? 'date'
                    : 'text'
                var isDate = field.type === 'date'
                control = el('input', {
                    id: id, name: field.key, type: inputType, class: 'fgform-input',
                    maxlength: isDate ? null : (field.max_length || 200),
                    placeholder: field.placeholder || '',
                    autocomplete: field.autocomplete || null,
                    required: field.required ? 'required' : null,
                })
                wrap.appendChild(control)
        }

        wrap.appendChild(el('p', { class: 'fgform-error', hidden: 'hidden' }))
        if (field.help_text) wrap.appendChild(el('p', { class: 'fgform-help' }, field.help_text))
        return wrap
    }

    // Render an array of field definitions into `container` (cleared first).
    function renderFields(container, fields, opts) {
        container.innerHTML = ''
        ;(fields || []).forEach(function (f) {
            container.appendChild(renderField(f, opts))
        })
        attachNameSplitHint(container)
    }

    // 姓 / 名 split check — a hint, never a blocking error. Applicants typed
    // their full name into 姓 and the given name again into 名, so mails greeted
    // them as 「中本達也 達也 様」. When 姓 ends with what is typed in 名, say so
    // under 姓. Only fires where a form has both `last_name` and `first_name`.
    function attachNameSplitHint(container) {
        var last = container.querySelector('[name="last_name"]')
        var first = container.querySelector('[name="first_name"]')
        if (!last || !first) return
        var hint = el('p', { class: 'fgform-help fgform-name-hint', hidden: 'hidden' },
            '姓の欄に名前まで入っていませんか？ 姓と名は分けて入力してください（例：姓「山田」・名「太郎」）')
        last.closest('.fgform-field').appendChild(hint)
        function check() {
            var l = last.value.replace(/[\s　]/g, '')
            var f = first.value.replace(/[\s　]/g, '')
            hint.hidden = !(f && l.length > f.length && l.slice(-f.length) === f)
        }
        last.addEventListener('input', check)
        first.addEventListener('input', check)
    }

    // Collect answers for `fields` from within `root`, keyed by field key.
    // checkbox → boolean; checkbox_group → array; others → trimmed string.
    function collectFields(root, fields) {
        var out = {}
        ;(fields || []).forEach(function (f) {
            if (f.type === 'checkbox') {
                var cb = root.querySelector('[name="' + cssEscape(f.key) + '"]')
                out[f.key] = !!(cb && cb.checked)
            } else if (f.type === 'checkbox_group') {
                var boxes = root.querySelectorAll('[data-checkbox-group="' + cssEscape(f.key) + '"]')
                var vals = []
                boxes.forEach(function (b) { if (b.checked) vals.push(b.value) })
                out[f.key] = vals
            } else {
                var input = root.querySelector('[name="' + cssEscape(f.key) + '"]')
                out[f.key] = input ? input.value.trim() : ''
            }
        })
        return out
    }

    // Lightweight client-side validation (the admin API re-validates
    // authoritatively). Shows inline errors; returns true when all pass.
    function validateFields(root, fields) {
        var ok = true
        var firstBad = null
        ;(fields || []).forEach(function (f) {
            var wrap = root.querySelector('.fgform-field[data-field-key="' + cssEscape(f.key) + '"]')
            if (!wrap || wrap.hidden) return // hidden by show_when → nothing to validate
            var errorEl = wrap.querySelector('.fgform-error')
            var msg = ''

            if (f.type === 'checkbox') {
                var cb = wrap.querySelector('input[type="checkbox"]')
                if (f.required && !(cb && cb.checked)) msg = 'チェックしてください'
            } else if (f.type === 'checkbox_group') {
                var boxes = wrap.querySelectorAll('[data-checkbox-group]')
                var any = false
                boxes.forEach(function (b) { if (b.checked) any = true })
                if (f.required && !any) msg = '1つ以上選択してください'
            } else if (f.type === 'file') {
                // The uploaded URL lives in the hidden companion; an empty one
                // means nothing was uploaded (or the upload failed).
                var fh = wrap.querySelector('input[type="hidden"][data-file-url]')
                if (f.required && !(fh && fh.value)) msg = 'ファイルをアップロードしてください'
            } else {
                var input = wrap.querySelector('input, select, textarea')
                if (input) {
                    var v = input.value.trim()
                    if (f.required && !v) msg = 'この項目は必須です'
                    else if (v && !input.checkValidity()) {
                        msg = input.type === 'email' ? '有効なメールアドレスを入力してください' : '入力内容を確認してください'
                    }
                }
            }

            if (msg) {
                ok = false
                if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false }
                if (!firstBad) firstBad = wrap
            } else if (errorEl) {
                errorEl.textContent = ''
                errorEl.hidden = true
            }
        })
        if (firstBad && firstBad.scrollIntoView) firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return ok
    }

    // POST JSON; resolve parsed body, reject Error(message) on !ok.
    function postJson(url, payload) {
        return fetch(url, {
            method: 'POST',
            headers: { 'content-type': 'application/json', accept: 'application/json' },
            body: JSON.stringify(payload),
        }).then(function (res) {
            return res.json().catch(function () { return {} }).then(function (body) {
                if (!res.ok) {
                    var err = new Error(body.error || ('HTTP ' + res.status))
                    err.code = body.code
                    throw err
                }
                return body
            })
        })
    }

    function getJson(url) {
        return fetch(url, { headers: { accept: 'application/json' } }).then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status)
            return res.json()
        })
    }

    // Minimal CSS.escape fallback for attribute selectors (field keys are
    // [A-Za-z0-9_-] per the contract, so this is belt-and-suspenders).
    function cssEscape(s) {
        if (window.CSS && CSS.escape) return CSS.escape(s)
        return String(s).replace(/["\\]/g, '\\$&')
    }

    // ---- Cart mode -------------------------------------------------------
    //
    // The other consumers of this framework POST JSON to the admin API. Paid
    // products can't do that — the answers have to ride along with Shopify's
    // add-to-cart request as line-item properties. Cart mode renders the same
    // field definitions so that each control:
    //
    //   * is named `properties[<label>]`, which is how Shopify turns an input
    //     into a line-item property (the LABEL is the property name, so it is
    //     what shows up in the cart and in the order admin), and
    //   * carries `form="<product_form_id>"`, the HTML attribute that submits an
    //     input with a form it is not nested inside — here the theme's buy
    //     (add-to-cart) form that the product-form block sits alongside. Since
    //     assets/product-form.js already gates submission on
    //     form.checkValidity(), `required` on these controls is enforced.
    //
    // Two field types can't be named directly because their value is a
    // composite: `checkbox` (one box → はい/empty) and `checkbox_group` (many
    // boxes → values joined with 「、」). Both
    // get a hidden input that carries the property and is kept in sync.

    var CHECKBOX_GROUP_SEPARATOR = '、'

    function propertyName(field) {
        return 'properties[' + (field.label || field.key) + ']'
    }

    function bindHidden(wrap, field, formId, compute) {
        var hidden = el('input', {
            type: 'hidden', name: propertyName(field), form: formId, value: '',
        })
        wrap.appendChild(hidden)
        var sync = function () { hidden.value = compute() }
        wrap.addEventListener('change', sync)
        sync()
        return hidden
    }

    // ---- Conditional fields (show_when) ---------------------------------
    //
    // A field may carry `show_when: { option, in: [values] }` — show it only while
    // the product option named `option` (e.g. プラン) is set to one of `in`. The
    // theme's variant picker is the source of truth: option name = a fieldset's
    // <legend>, selected value = its checked radio (or <select>). Hidden fields are
    // also DISABLED: a disabled control is left out of the add-to-cart FormData
    // (no stale properties[…]) and out of constraint validation (a hidden
    // `required` field cannot block the buy button).
    //
    // Fail open: with no picker, or an option name the picker doesn't have, the
    // field stays visible — never drop an applicant's answer on a lookup miss.

    function selectedProductOptions() {
        var picker = document.querySelector('variant-picker')
        if (!picker) return null
        var out = {}
        picker.querySelectorAll('fieldset').forEach(function (fs) {
            var legend = fs.querySelector('legend')
            var checked = fs.querySelector('input:checked')
            var select = fs.querySelector('select')
            var value = checked ? checked.value : (select ? select.value : null)
            if (legend && value != null) out[legend.textContent.trim()] = value
        })
        return out
    }

    function applyShowWhen(container, fields) {
        var conditional = (fields || []).filter(function (f) { return f.show_when && f.show_when.option })
        if (container._fgShowWhen) {
            document.removeEventListener('variant:selected', container._fgShowWhen)
            container._fgShowWhen = null
        }
        if (!conditional.length) return

        function update() {
            var selected = selectedProductOptions()
            conditional.forEach(function (f) {
                var wrap = container.querySelector('.fgform-field[data-field-key="' + cssEscape(f.key) + '"]')
                if (!wrap) return
                var cond = f.show_when
                var known = selected && Object.prototype.hasOwnProperty.call(selected, cond.option)
                var visible = !known || (cond.in || []).indexOf(selected[cond.option]) !== -1
                wrap.hidden = !visible
                wrap.querySelectorAll('input, select, textarea').forEach(function (c) { c.disabled = !visible })
            })
        }
        // The picker dispatches this after it updates the checked option; it
        // bubbles to document. No event fires on load, so evaluate once now.
        container._fgShowWhen = update
        document.addEventListener('variant:selected', update)
        update()
    }

    // Render `fields` into `container` as line-item property inputs for `formId`.
    function renderFieldsForCart(container, fields, opts) {
        opts = opts || {}
        var formId = opts.formId
        if (!formId) throw new Error('FG_FORMS.renderFieldsForCart requires opts.formId')

        container.innerHTML = ''
        ;(fields || []).forEach(function (field) {
            var wrap = renderField(field, opts)

            if (field.type === 'checkbox') {
                var box = wrap.querySelector('input[type="checkbox"]')
                if (box) {
                    // No `name` → the box itself is never submitted; the hidden
                    // input carries the value. It still needs `form` so that a
                    // required box participates in form.checkValidity().
                    box.removeAttribute('name')
                    box.setAttribute('form', formId)
                    bindHidden(wrap, field, formId, function () { return box.checked ? 'はい' : '' })
                }
            } else if (field.type === 'checkbox_group') {
                var boxes = wrap.querySelectorAll('[data-checkbox-group]')
                bindHidden(wrap, field, formId, function () {
                    var vals = []
                    Array.prototype.forEach.call(boxes, function (b) { if (b.checked) vals.push(b.value) })
                    return vals.join(CHECKBOX_GROUP_SEPARATOR)
                })
                // Native `required` can't express "at least one of these". Put it
                // on the first box and lift it as soon as any box is checked, so
                // form.checkValidity() still blocks an empty required group. The
                // box must be form-associated to be validated at all, and stays
                // nameless so it is not submitted alongside the hidden input.
                if (field.required && boxes.length) {
                    var first = boxes[0]
                    first.setAttribute('form', formId)
                    first.setAttribute('required', 'required')
                    wrap.addEventListener('change', function () {
                        var any = Array.prototype.some.call(boxes, function (b) { return b.checked })
                        if (any) first.removeAttribute('required')
                        else first.setAttribute('required', 'required')
                    })
                }
            } else if (field.type === 'file') {
                // The hidden companion carries the property (the uploaded URL);
                // the file input stays nameless so the raw file is never posted.
                // Both must be form-associated so a required file participates in
                // form.checkValidity() and the URL submits with add-to-cart.
                var fileHidden = wrap.querySelector('input[type="hidden"][data-file-url]')
                var fileControl = wrap.querySelector('input[type="file"]')
                if (fileHidden) {
                    fileHidden.name = propertyName(field)
                    fileHidden.setAttribute('form', formId)
                }
                if (fileControl) fileControl.setAttribute('form', formId)
            } else {
                var control = wrap.querySelector('input, select, textarea')
                if (control) {
                    control.name = propertyName(field)
                    control.setAttribute('form', formId)
                }
            }

            container.appendChild(wrap)
        })
        applyShowWhen(container, fields)
    }

    window.FG_FORMS = {
        renderField: renderField,
        renderFields: renderFields,
        renderFieldsForCart: renderFieldsForCart,
        collectFields: collectFields,
        validateFields: validateFields,
        postJson: postJson,
        getJson: getJson,
    }
})()
